import React, { useMemo } from 'react'
import { Checkbox, Table } from 'antd'
import type { ColumnsType } from 'antd/es/table'

export interface AccessCollaborator {
  coll_id: string
  prename: string
  lastname: string
  rank: string
}

export interface CollAccessChild {
  coll_id: string
  checked: boolean
}

export interface CollAccessState {
  collschecked: CollAccessChild[]
}

interface AppCollAccessesProps {
  colls: AccessCollaborator[]
  value?: string | null
  onChange?: (datajson: string) => void
}

function parseValue(raw?: string | null): CollAccessState {
  if (!raw) {
    return { collschecked: [] }
  }
  try {
    const parsed = JSON.parse(raw) as CollAccessState
    return {
      collschecked: Array.isArray(parsed.collschecked) ? parsed.collschecked : [],
    }
  } catch {
    return { collschecked: [] }
  }
}

export function AppCollAccesses({ colls, value, onChange }: AppCollAccessesProps) {
  const state = useMemo(() => parseValue(value), [value])

  const sortedColls = useMemo(
    () =>
      [...colls].sort(
        (a, b) => parseInt(a.rank, 10) - parseInt(b.rank, 10),
      ),
    [colls],
  )

  const allChecked =
    sortedColls.length > 0 &&
    sortedColls.every(
      coll => state.collschecked.find(e => e.coll_id === coll.coll_id)?.checked === true,
    )
  const someChecked = sortedColls.some(
    coll => state.collschecked.find(e => e.coll_id === coll.coll_id)?.checked === true,
  )

  const columns: ColumnsType<AccessCollaborator> = [
    {
      title: (
        <Checkbox
          checked={allChecked}
          indeterminate={someChecked && !allChecked}
          onChange={evt => {
            const checked = evt.target.checked
            onChange?.(
              JSON.stringify({
                collschecked: sortedColls.map(coll => ({
                  coll_id: coll.coll_id,
                  checked,
                })),
              }),
            )
          }}
          title={allChecked ? 'Tout désélectionner' : 'Tout sélectionner'}
        />
      ),
      key: 'checked',
      width: 60,
      render: (_, coll) => {
        const checked = state.collschecked.find(e => e.coll_id === coll.coll_id)?.checked ?? false
        return (
          <Checkbox
            checked={checked}
            onChange={evt => {
              const exists = state.collschecked.find(e => e.coll_id === coll.coll_id)
              const collschecked = exists
                ? state.collschecked.map(e =>
                    e.coll_id === coll.coll_id ? { ...e, checked: evt.target.checked } : e,
                  )
                : state.collschecked.concat([
                    { coll_id: coll.coll_id, checked: evt.target.checked },
                  ])
              onChange?.(JSON.stringify({ collschecked }))
            }}
          />
        )
      },
    },
    {
      title: 'Collaborateur',
      key: 'name',
      render: (_, coll) => `${coll.prename} ${coll.lastname}`,
    },
    {
      title: 'Rang',
      dataIndex: 'rank',
      key: 'rank',
      width: 80,
    },
  ]

  return (
    <Table
      rowKey="coll_id"
      size="small"
      pagination={false}
      columns={columns}
      dataSource={sortedColls}
    />
  )
}
